import React, {useState} from "react";
import {Form} from "react-bootstrap";

/**
 * A `Form.Control` for numeric fields, used as `as={NumberFormControl}` on a Formik Field.
 * Number inputs change their value when you scroll over a focused field, so just scrolling
 * the page silently edits numbers. Therefore we render a text input with a numeric keypad
 * (`inputMode`) instead, which has no wheel handling and also no spinner arrows.
 *
 * Formik should still store real numbers, so we forward the change event with
 * `target.type: "number"` and let Formik's own handleChange parse it, exactly like it did for the
 * real number input. We normalize the raw text into a plain number string first.
 */
export default function NumberFormControl({value, onChange, ...props}: React.ComponentProps<typeof Form.Control>) {
    const externalValue = value == null || value === "" ? "" : Number(value);

    /**
     * We keep the raw text in local state so that typing "3," survives the re-render after
     * Formik stored the parsed number. A fully controlled text input would snap back to "3"
     * and eat the decimal separator. If the outside value stops matching the text (initial
     * load, form reinitialize), we take over the outside value.
     */
    const [text, setText] = useState(externalValue === "" ? "" : String(externalValue));
    const parsedText = parseFloat(text.replace(",", "."));
    if ((isNaN(parsedText) ? "" : parsedText) !== externalValue) {
        setText(externalValue === "" ? "" : String(externalValue));
    }

    return (
        <Form.Control
            {...props}
            type="text"
            inputMode="decimal"
            value={text}
            onChange={event => {
                const normalizedValue = event.target.value
                    /**
                     * Use a period as the decimal separator (parseFloat would otherwise stop at a comma).
                     */
                    .replace(/,/g, ".")
                    /**
                     * Only digits, a decimal separator, and a minus, so letters can't be typed.
                     */
                    .replace(/[^0-9.-]/g, "")
                    /**
                     * Keep only a leading minus.
                     */
                    .replace(/(?!^)-/g, "")
                    /**
                     * Only a single decimal separator.
                     */
                    .replace(/(\..*)\./g, "$1");
                setText(normalizedValue);
                onChange?.({
                    target: {
                        name: event.target.name,
                        type: "number",
                        value: normalizedValue,
                    },
                } as unknown as React.ChangeEvent<any>);
            }}
        />
    )
}
